import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { copyAndOpenGoogleDoc, getPasteShortcut } from "@/lib/exportGoogleDoc";
import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";

type GoogleDocExportButtonProps = {
  html?: string;
  plain: string;
  docTitle: string;
  label?: string;
  dialogTitle?: string;
  size?: VariantProps<typeof buttonVariants>["size"];
  variant?: VariantProps<typeof buttonVariants>["variant"];
  className?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export function GoogleDocExportButton({
  html,
  plain,
  docTitle,
  label = "Copy & open Google Doc",
  dialogTitle = "Copy into a Google Doc",
  size = "sm",
  variant = "outline",
  className,
  disabled = false,
  onClick,
}: GoogleDocExportButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const pasteShortcut = getPasteShortcut();
  const iconClassName =
    size === "default" || size === "lg" ? "w-4 h-4 mr-2" : "w-3 h-3 mr-1";

  const handleConfirm = async () => {
    const result = await copyAndOpenGoogleDoc({ html, plain, docTitle });
    if (!result.ok) {
      toast({
        title: "Could not copy to clipboard",
        description: "Try using the Download PDF or Print option instead.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Copied — paste into the Google Doc",
      description: `In the new tab, click inside the doc and press ${pasteShortcut}.`,
    });
    setShowConfirm(false);
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled}
        className={cn(className)}
        onClick={(event) => {
          onClick?.(event);
          setShowConfirm(true);
        }}
      >
        <ExternalLink className={iconClassName} />
        {label}
      </Button>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Google Docs opens in a new tab. Your browser does not let us paste into it
                  automatically — you will need one quick paste step.
                </p>
                <ol className="list-decimal list-inside space-y-1.5 text-gray-700">
                  <li>
                    Click <span className="font-medium">Copy &amp; open</span> — content is copied
                    to your clipboard
                  </li>
                  <li>A blank Google Doc opens in a new tab</li>
                  <li>
                    Click inside the doc and press{" "}
                    <kbd className="px-1 py-0.5 rounded border border-gray-200 bg-gray-50 font-mono text-[11px]">
                      {pasteShortcut}
                    </kbd>
                  </li>
                </ol>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
              onClick={(event) => {
                event.preventDefault();
                void handleConfirm();
              }}
            >
              Copy &amp; open
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
